import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ShieldCheck,
  LogOut,
  Pencil,
  Trash2,
  ExternalLink,
  Search,
  Plus,
  Download,
  IdCard,
  Users as UsersIcon,
  UserCog,
  MapPin,
} from "lucide-react";
import { downloadBadgePng } from "@/lib/renderBadge";
import UsersPanel from "./UsersPanel";
import StaffPanel from "./StaffPanel";
import CitiesPanel from "./CitiesPanel";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const TOKEN_KEY = "badgeAdminToken";
const VERIFY_ORIGIN =
  process.env.REACT_APP_BACKEND_URL || window.location.origin;

/* ---------------------------------------------------------------------- */
/*  Login gate                                                            */
/* ---------------------------------------------------------------------- */

function LoginGate({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/badge/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        setError("Incorrect password");
        return;
      }
      const data = await res.json();
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      setError("Network error — try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 pt-24 sm:pt-28 pb-8">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-5 h-5 text-brand-navy" />
          <h1 className="text-lg font-bold text-brand-navy">
            Admin
          </h1>
        </div>
        <p className="text-xs text-slate-500 mb-6">
          Restricted to authorized HepCURE staff.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="password" className="text-xs">
              Admin password
            </Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              data-testid="badge-admin-password"
              className="mt-1"
            />
          </div>
          {error && (
            <p
              className="text-xs text-red-600"
              data-testid="badge-admin-error"
            >
              {error}
            </p>
          )}
          <Button
            type="submit"
            disabled={loading || !password}
            data-testid="badge-admin-login-btn"
            className="w-full bg-brand-navy hover:bg-brand-navy-dark text-white"
          >
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/*  Admin list                                                            */
/* ---------------------------------------------------------------------- */

function AdminList({ token, onLogout }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(null);
  const [tab, setTab] = useState(() => {
    // Persist tab selection across reloads for convenience
    try {
      return localStorage.getItem("badgeAdminTab") || "badges";
    } catch (_) {
      return "badges";
    }
  });
  const navigate = useNavigate();

  useEffect(() => {
    try { localStorage.setItem("badgeAdminTab", tab); } catch (_) { /* ignore */ }
  }, [tab]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/badge/employees`, {
        headers: { "X-Auth-Token": token },
      });
      if (res.status === 401) {
        onLogout();
        return;
      }
      const data = await res.json();
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [token]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (e) =>
        e.id_number?.toLowerCase().includes(q) ||
        e.name?.toLowerCase().includes(q) ||
        e.department?.toLowerCase().includes(q) ||
        e.role?.toLowerCase().includes(q)
    );
  }, [items, query]);

  const deleteEmployee = async (id_number) => {
    if (!confirm(`Delete ${id_number}?`)) return;
    setBusy(id_number);
    try {
      await fetch(`${API}/badge/employees/${id_number}`, {
        method: "DELETE",
        headers: { "X-Auth-Token": token },
      });
      await load();
    } finally {
      setBusy(null);
    }
  };

  const openVerify = (id) =>
    navigate(`/verify?id=${encodeURIComponent(id)}&from=admin`);

  const openEditor = (id) =>
    navigate(`/badge?edit=${encodeURIComponent(id)}`);

  // Download 4K PNG of a saved badge directly from the click handler.
  // This must run synchronously in the click gesture so iOS Safari permits
  // the download — we cannot navigate away and fire the download later.
  const [downloadingId, setDownloadingId] = useState(null);
  const downloadBadge = async (emp) => {
    if (downloadingId) return;
    setDownloadingId(emp.id_number);
    try {
      // Resolve photo_url to an absolute URL so the Image() load works no
      // matter what origin the app was loaded from.
      const photoUrl = emp.photo_url
        ? emp.photo_url.startsWith("http")
          ? emp.photo_url
          : `${process.env.REACT_APP_BACKEND_URL || window.location.origin}${emp.photo_url}?t=${Date.now()}`
        : "";
      await downloadBadgePng({
        employee: emp,
        photoUrl,
        verifyOrigin: VERIFY_ORIGIN,
      });
    } catch (err) {
      console.error(err);
      toast.error("Download failed — check console");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 pt-24 sm:pt-28 pb-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-brand-navy">
              Admin console
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {tab === "badges"
                ? "Create, edit, and delete HepCURE employee ID records. Records feed the QR verification endpoint."
                : "Manage platform users, their PINs, and which sections they can unlock."}
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            {tab === "badges" && (
              <Button
                onClick={() => navigate("/badge")}
                data-testid="badge-admin-new-btn"
                className="bg-brand-teal hover:bg-brand-teal-dark text-white"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                New badge
              </Button>
            )}
            <Button
              variant="outline"
              onClick={onLogout}
              data-testid="badge-admin-logout"
            >
              <LogOut className="w-4 h-4 mr-1.5" />
              Sign out
            </Button>
          </div>
        </div>

        {/* --- Tab bar --- */}
        <div className="mb-6 inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
          <button
            type="button"
            onClick={() => setTab("badges")}
            data-testid="admin-tab-badges"
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === "badges"
                ? "bg-brand-navy text-white"
                : "text-slate-600 hover:text-brand-navy"
            }`}
          >
            <IdCard className="w-4 h-4" />
            Badges
          </button>
          <button
            type="button"
            onClick={() => setTab("users")}
            data-testid="admin-tab-users"
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === "users"
                ? "bg-brand-navy text-white"
                : "text-slate-600 hover:text-brand-navy"
            }`}
          >
            <UsersIcon className="w-4 h-4" />
            Users
          </button>
          <button
            type="button"
            onClick={() => setTab("staff")}
            data-testid="admin-tab-staff"
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === "staff"
                ? "bg-brand-navy text-white"
                : "text-slate-600 hover:text-brand-navy"
            }`}
          >
            <UserCog className="w-4 h-4" />
            Staff
          </button>
          <button
            type="button"
            onClick={() => setTab("cities")}
            data-testid="admin-tab-cities"
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === "cities"
                ? "bg-brand-navy text-white"
                : "text-slate-600 hover:text-brand-navy"
            }`}
          >
            <MapPin className="w-4 h-4" />
            Cities
          </button>
        </div>

        {tab === "users" ? (
          <UsersPanel badgeToken={token} />
        ) : tab === "staff" ? (
          <StaffPanel badgeToken={token} />
        ) : tab === "cities" ? (
          <CitiesPanel badgeToken={token} />
        ) : (
        <>
        <div className="mb-4 relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by name, ID, department…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
            data-testid="badge-admin-search"
          />
        </div>

        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="bg-slate-100 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-semibold whitespace-nowrap">ID</th>
                <th className="text-left px-4 py-3 font-semibold whitespace-nowrap">Name</th>
                <th className="text-left px-4 py-3 font-semibold whitespace-nowrap">
                  Department
                </th>
                <th className="text-left px-4 py-3 font-semibold whitespace-nowrap">Expires</th>
                <th className="text-right px-4 py-3 font-semibold whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody data-testid="badge-admin-list">
              {loading && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-10 text-center text-slate-400"
                  >
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-10 text-center text-slate-400"
                  >
                    No employees yet — click <b>New badge</b> to add one.
                  </td>
                </tr>
              )}
              {filtered.map((e) => (
                <tr
                  key={e.id_number}
                  className="border-t border-slate-100 hover:bg-slate-50"
                  data-testid={`badge-admin-row-${e.id_number}`}
                >
                  <td className="px-4 py-3 font-mono text-xs text-slate-700 whitespace-nowrap">
                    {e.id_number}
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900 whitespace-nowrap">
                    {e.name}
                  </td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                    {e.department}
                  </td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{e.expires}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="inline-flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openVerify(e.id_number)}
                        data-testid={`badge-admin-verify-${e.id_number}`}
                        title="Open verify page"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadBadge(e)}
                        disabled={downloadingId === e.id_number}
                        data-testid={`badge-admin-download-${e.id_number}`}
                        title="Download 4K PNG"
                        className="text-brand-teal hover:bg-emerald-50"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditor(e.id_number)}
                        data-testid={`badge-admin-edit-${e.id_number}`}
                        title="Edit record"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteEmployee(e.id_number)}
                        disabled={busy === e.id_number}
                        data-testid={`badge-admin-delete-${e.id_number}`}
                        title="Delete record"
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/*  Page                                                                  */
/* ---------------------------------------------------------------------- */

export default function BadgeAdmin() {
  const [token, setToken] = useState(
    () => localStorage.getItem(TOKEN_KEY) || ""
  );

  const logout = () => {
    fetch(`${API}/badge/admin/logout`, {
      method: "POST",
      headers: { "X-Auth-Token": token },
    }).catch(() => {});
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  if (!token) return <LoginGate onLogin={setToken} />;
  return <AdminList token={token} onLogout={logout} />;
}
